define((require) => {
    const _                         = require("underscore");
    const EventDispatcher           = require("shared/util/EventDispatcher");
    const GuidelineCompositeView    = require("ui/editor/guideline/GuidelineCompositeView");
    const GuidelineModel            = require("ui/editor/guideline/GuidelineModel");
    const HorizontalRulerView       = require("ui/editor/ruler/HorizontalRulerView");
    const VerticalRulerView         = require("ui/editor/ruler/VerticalRulerView");

    return EventDispatcher.extend({

        viewModel: null,

        viewScope: null,

        sceneEditor: null,

        horizontalRuler: null,

        verticalRuler: null,

        _firstRender: true,

        _zoomModel: null,

        constructor: function(options) {
            this.viewScope = options.viewScope;
            this.sceneEditor = options.sceneEditor;
            this.viewModel = this.viewScope.viewModel;
            this._zoomModel = this.viewModel.get("zoomModel");
            this.horizontalRuler = new HorizontalRulerView();
            this.verticalRuler = new VerticalRulerView();

            this.guidelineCompositeView = new GuidelineCompositeView({
                collection: this.sceneEditor.model.get("guidelines"),
                viewModel: this.viewModel,
                sceneEditor: this.sceneEditor
            });
        },

        render: function() {
            // on a scene width/height change, the rulers elements are removed from the DOM
            // so re-set them here to elements that are newly in the DOM.
            this.horizontalRuler.setElement(this.sceneEditor.ui.horizontalRuler);
            this.verticalRuler.setElement(this.sceneEditor.ui.verticalRuler);
            this.horizontalRuler.render(this.sceneEditor.experienceModel.get("width"));
            this.verticalRuler.render(this.sceneEditor.experienceModel.get("height"));

            // Needed in order to have the ruler not show up at wrong place at first
            this.horizontalRuler.$el.css("top", 0);
            this._getHorizontalRulerMask().css("top", 0);
            this._getVerticalRulerMask().css("top", 0);

            // This is a compensation for this collection view not being rendered properly with its children views
            if (!this.sceneEditor._isShown) {
                this._addEventListeners();
            } else {
                // On re-render the transformer event listener needs to be re-attached
                this.sceneEditor.sceneWidgetCollectionView.off("transforming", this._setMarkers.bind(this));
                this.sceneEditor.sceneWidgetCollectionView.on("transforming", this._setMarkers.bind(this));
            }

            this._renderGuidelines();

            this.listenTo(this.viewModel, "change:currentLayer change:sceneNumber", this._renderGuidelines);
            this.listenTo(this.sceneEditor.experienceModel, "change:width change:height", this._repositionRulers);

            this._firstRender = false;
        },

        _renderGuidelines() {
            if (this.guidelineCompositeView && this.sceneEditor.guidelinesContainer) {
                this.sceneEditor.guidelinesContainer.close();
            }

            const guidelineCollection = this.viewModel.get("activeLayerSceneModel").get("guidelines");


            this.guidelineCompositeView = new GuidelineCompositeView({
                collection: guidelineCollection,
                viewModel: this.viewModel,
                sceneEditor: this.sceneEditor
            });

            if (this.sceneEditor.guidelinesContainer) {
                this.sceneEditor.guidelinesContainer.show(this.guidelineCompositeView);
            }
        },

        _addEventListeners: function() {
            // Connect to scrollbars event.
            this.viewScope.getScrollbarV().on("move", this._repositionRulers.bind(this));
            this.viewScope.getScrollbarH().on("move", this._repositionRulers.bind(this));

            this.viewModel.selection.on("change:widgets", this._changeSelectedWidgetsHandler.bind(this));
            this.sceneEditor.listenTo(this.viewModel, "change:activeWidgetCords", this._repositionRulers.bind(this));

            // Connect ruler to scale change and adding new scene.
            this.sceneEditor.listenTo(this._zoomModel, "change:scale", this._repositionRulers.bind(this));
            this.sceneEditor.listenTo(this.sceneEditor.experienceModel.get("scenes"), "add", this._repositionRulers.bind(this));

            this.sceneEditor.sceneWidgetCollectionView.on("transforming", this._setMarkers.bind(this));

            this.sceneEditor.listenTo(this.getHorizontalRuler(), "guideline:add", (mouseDownEvent) => {
                this.guidelineCompositeView.addGuideline(GuidelineModel.HORIZONTAL, mouseDownEvent);
            });

            this.sceneEditor.listenTo(this.getVerticalRuler(), "guideline:add", (mouseDownEvent) => {
                this.guidelineCompositeView.addGuideline(GuidelineModel.VERTICAL, mouseDownEvent);
            });
        },

        _repositionHorizontalRuler: function() {
            var topOffset = this.viewScope.getViewportElement().get(0).getBoundingClientRect().top - this.sceneEditor.el.getBoundingClientRect().top,
                rulerTop = Math.floor(topOffset * (1 / this._zoomModel.get("scale")));

            this._getVerticalRulerMask().css("top", rulerTop);

            if (this.sceneEditor.isCurrentScene()) {
                this.horizontalRuler.$el.css("top", rulerTop);
            } else {
                this._getHorizontalRulerMask().css("top", rulerTop);
            }
        },

        _repositionVerticalRuler: function() {
            if (!this.sceneEditor.isCurrentScene()) {
                return;
            }

            var sceneOffset = this.sceneEditor.$el.offset(),
                rulerOffset = this._getRulerOffset(sceneOffset);

            // Make sure the ruler doesn't move out of the scene borders
            if (rulerOffset <= (this.sceneEditor.$el.width() - this.verticalRuler.$el.width())) {
                this.verticalRuler.$el.css("left", rulerOffset);
            }
        },

        _getRulerOffset: function(sceneOffset) {
            if (sceneOffset.left > 0) {
                return 0;
            }
            return Math.floor(sceneOffset.left * (-1 / this._zoomModel.get("scale")));
        },

        _changeSelectedWidgetsHandler: function(selection) {
            if (!this.sceneEditor.isCurrentScene()) {
                return;
            }

            var previouslySelectedWidgets = selection.previousAttributes().widgets;

            // clear events off of previously selected widgets
            if (previouslySelectedWidgets) {
                _.each(previouslySelectedWidgets, (previousWidget) => {
                    this.sceneEditor.stopListening(previousWidget.model, "change", this._setMarkers);
                });
            }

            // put events for position changing on newly selected widgets
            selection.get("widgets").forEach((selectedWidget) => {
                this.sceneEditor.listenTo(selectedWidget.model, "change", this._setMarkers.bind(this));
            });

            this._setMarkers();
        },

        _setMarkers: function() {
            var sceneEditorOffset = this.sceneEditor.$el.offset();
            var rulerWidth = this.verticalRuler.$el.width();

            var elementsBounds = _.map(this.viewModel.selection.get("widgets"), (widget) => {
                var boundingBoxGlobal = widget.getBoundingBox(true);

                var boundingBoxLocal = {
                    left: Math.round((boundingBoxGlobal.left - sceneEditorOffset.left) / this._zoomModel.get("scale") - rulerWidth),
                    top: Math.round((boundingBoxGlobal.top - sceneEditorOffset.top) / this._zoomModel.get("scale") - rulerWidth),
                    bottom: Math.round((boundingBoxGlobal.bottom - sceneEditorOffset.top) / this._zoomModel.get("scale") - rulerWidth),
                    right: Math.round((boundingBoxGlobal.right - sceneEditorOffset.left) / this._zoomModel.get("scale") - rulerWidth)
                };

                return boundingBoxLocal;
            });

            if (elementsBounds.length === 0) {
                this.horizontalRuler.setMarkers([]);
                this.verticalRuler.setMarkers([]);
                return;
            }

            // outermost left, top, right and bottom values from the list of `selectedTransformableElements`
            var bounds = {
                left: _.min(_.pluck(elementsBounds, "left")),
                top: _.min(_.pluck(elementsBounds, "top")),
                right: _.max(_.pluck(elementsBounds, "right")),
                bottom: _.max(_.pluck(elementsBounds, "bottom"))
            };

            if (this.horizontalRuler) {
                this.horizontalRuler.setMarkers([bounds.left, bounds.right]);
            }

            if (this.verticalRuler) {
                this.verticalRuler.setMarkers([bounds.top, bounds.bottom]);
            }
        },

        /**
         * Re-align rulers (top/side), and show if the current scene, otherwise hide rulers.
         */
        _repositionRulers: function() {
            this._repositionHorizontalRuler();
            this._repositionVerticalRuler();
        },

        getHorizontalRuler: function() {
            return this.horizontalRuler;
        },

        getVerticalRuler: function() {
            return this.verticalRuler;
        },

        _getHorizontalRulerMask: function() {
            return this.sceneEditor.$el.find(".horizontal-ruler-mask");
        },

        _getVerticalRulerMask: function() {
            return this.sceneEditor.$el.find(".vertical-ruler-mask");
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/editor/ruler/SceneRulers.js